import { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import SearchIcon from '@mui/icons-material/Search'
import CloseIcon from '@mui/icons-material/Close'
import CheckIcon from '@mui/icons-material/Check'
import TuneIcon from '@mui/icons-material/Tune'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined'
import useDebounce from 'utils/hooks/useDebounce'
import { InvolvesChip, RaisedChip, ResolvedChip } from './FilterChips'
import OwnerPicker from './OwnerPicker'
import Popover from './Popover'
import { InlineLabel, Pill, PillCount, PillSelect } from './FilterControls'
import { formatDay } from './remedialDates'
import { SEVERITY_ORDER, severityMeta, sourceLabel, stageMeta } from './remedialMeta'
import { SORT_OPTIONS, SOURCES, activeFilterCount, hasActiveFilters, stageOptionsForView } from './remedialsQuery'
import { ACCENT, BORDER, INK, MUTED, SecondaryButton } from './ui'

const SOURCE_OPTIONS = SOURCES.map(source => ({ value: source, label: sourceLabel(source) }))

const COMMENT_OPTIONS = [
  { value: 'any', label: 'Any' },
  { value: 'with', label: 'Has comments' },
  { value: 'without', label: 'No comments' },
]

const dueLabel = (from, to) => {
  if (from && to) return `Due ${formatDay(from)} to ${formatDay(to)}`
  if (from) return `Due from ${formatDay(from)}`
  if (to) return `Due by ${formatDay(to)}`
  return 'Due date'
}

const TableFilters = ({
  view, filters, sort, ownerName, buildingId, scopeFilters = null, viewPicker = null, exportCount = 0,
  onChange, onClear, onSortChoose, onExport,
}) => {
  const [search, setSearch] = useState(filters.q)
  const debouncedSearch = useDebounce(search, 300)
  const pushed = useRef(filters.q)
  const ownerRef = useRef(null)
  const dueRef = useRef(null)
  const [ownerOpen, setOwnerOpen] = useState(false)
  const [dueOpen, setDueOpen] = useState(false)
  const [pickedName, setPickedName] = useState(null)
  const [mobileOpen, setMobileOpen] = useState(false)
  const filterCount = activeFilterCount(filters)
  const filtered = hasActiveFilters(filters)

  useEffect(() => {
    if (filters.q !== pushed.current) {
      pushed.current = filters.q
      setSearch(filters.q)
    }
  }, [filters.q])

  useEffect(() => {
    if (debouncedSearch !== pushed.current) {
      pushed.current = debouncedSearch
      onChange({ q: debouncedSearch })
    }
  }, [debouncedSearch, onChange])

  const stageChoices = stageOptionsForView(view)
  const ownerLabel = filters.owner ? (pickedName || ownerName || 'Chosen person') : 'Owner'
  const dueActive = !!(filters.dueFrom || filters.dueTo)

  const toggleSeverity = level => {
    const next = filters.severity.includes(level)
      ? filters.severity.filter(value => value !== level)
      : [...filters.severity, level]
    onChange({ severity: SEVERITY_ORDER.filter(value => next.includes(value)) })
  }

  return (
    <Bar>
      <TopRow>
        {viewPicker}
        <SearchWrap>
          <SearchIcon style={{ fontSize: 20, color: MUTED }} aria-hidden='true' />
          <SearchInput
            type='search'
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder='Search titles, descriptions and comments'
            aria-label='Search remedials by title, description or comment'
          />
        </SearchWrap>
        <FiltersToggle
          type='button'
          $active={filterCount > 0}
          onClick={() => setMobileOpen(open => !open)}
          aria-expanded={mobileOpen}
          aria-controls='remedials-filter-fields'
        >
          <TuneIcon aria-hidden='true' />
          Filters
          {filterCount > 0 && <PillCount>{filterCount}</PillCount>}
        </FiltersToggle>
        <ExportButton
          type='button'
          onClick={onExport}
          disabled={!exportCount}
          aria-label={`Export the ${exportCount} loaded remedial${exportCount === 1 ? '' : 's'} as a spreadsheet (CSV)`}
          title='Download the remedials loaded below as a spreadsheet (CSV)'
          data-tour-id='remedials.export-btn'
        >
          <FileDownloadOutlinedIcon aria-hidden='true' />
          <span>Export</span>
        </ExportButton>
      </TopRow>

      <Fields id='remedials-filter-fields' $open={mobileOpen}>
        <SeverityGroup role='group' aria-label='Filter by severity'>
          <InlineLabel aria-hidden='true'>Severity</InlineLabel>
          {SEVERITY_ORDER.map(level => {
            const meta = severityMeta(level)
            const on = filters.severity.includes(level)
            return (
              <SeverityChip
                key={level}
                type='button'
                aria-pressed={on}
                title={meta.text}
                onClick={() => toggleSeverity(level)}
                style={on ? { background: meta.bg, borderColor: meta.color, color: meta.color } : undefined}
              >
                {on ? <CheckIcon aria-hidden='true' style={{ fontSize: 16 }} /> : <Dot style={{ background: meta.color }} aria-hidden='true' />}
                {meta.label}
              </SeverityChip>
            )
          })}
        </SeverityGroup>

        <Divider aria-hidden='true' />

        <PillSelect
          multiple
          $active={filters.source.length > 0}
          value={filters.source}
          onChange={value => onChange({ source: value })}
          options={SOURCE_OPTIONS}
          placeholder='Source'
          searchable={false}
          ariaLabel='Filter by source'
        />
        {stageChoices.length > 1 && (
          <PillSelect
            multiple
            $active={filters.stage.some(stage => stageChoices.includes(stage))}
            value={filters.stage.filter(stage => stageChoices.includes(stage))}
            onChange={value => onChange({ stage: value })}
            options={stageChoices.map(stage => ({ value: stage, label: stageMeta(stage).label, color: stageMeta(stage).color }))}
            placeholder='Stage'
            searchable={false}
            ariaLabel='Filter by stage'
          />
        )}
        <Pill
          ref={ownerRef}
          type='button'
          $active={!!filters.owner}
          onClick={() => setOwnerOpen(true)}
          aria-haspopup='dialog'
          aria-label={`Filter by owner: ${filters.owner ? ownerLabel : 'Anyone'}`}
        >
          <PersonOutlineIcon aria-hidden='true' />
          <PillText>{ownerLabel}</PillText>
        </Pill>
        <OwnerPicker
          anchorEl={ownerRef.current}
          open={ownerOpen}
          onClose={() => setOwnerOpen(false)}
          buildingPassportId={buildingId}
          currentOwnerId={filters.owner}
          clearLabel='Anyone'
          title='Show remedials owned by'
          onSelect={person => {
            setOwnerOpen(false)
            setPickedName(person ? person.name : null)
            onChange({ owner: person ? person.id : null })
          }}
        />
        <PillSelect
          $active={!!filters.comments}
          value={filters.comments}
          onChange={value => onChange({ comments: value === 'with' || value === 'without' ? value : null })}
          options={COMMENT_OPTIONS}
          placeholder='Comments'
          searchable={false}
          ariaLabel='Filter by comments'
        />
        {scopeFilters}
        <Pill
          ref={dueRef}
          type='button'
          $active={dueActive}
          onClick={() => setDueOpen(open => !open)}
          aria-haspopup='dialog'
          aria-expanded={dueOpen}
        >
          <CalendarTodayIcon aria-hidden='true' style={{ fontSize: 16 }} />
          <PillText>{dueLabel(filters.dueFrom, filters.dueTo)}</PillText>
        </Pill>
        <Popover anchorEl={dueRef.current} open={dueOpen} onClose={() => setDueOpen(false)} label='Filter by due date' width={300} closeLabel='Close the due date filter'>
          <PopTitle>Filter by due date</PopTitle>
          <PopHelp>Show remedials due between these dates. Leave one empty to have no limit on that side.</PopHelp>
          <DateField>
            <DateLabel htmlFor='remedials-due-from'>Due from</DateLabel>
            <DateInput id='remedials-due-from' type='date' value={filters.dueFrom || ''} onChange={e => onChange({ dueFrom: e.target.value || null })} />
          </DateField>
          <DateField>
            <DateLabel htmlFor='remedials-due-to'>Due by</DateLabel>
            <DateInput id='remedials-due-to' type='date' value={filters.dueTo || ''} onChange={e => onChange({ dueTo: e.target.value || null })} />
          </DateField>
          {dueActive && (
            <SecondaryButton type='button' onClick={() => onChange({ dueFrom: null, dueTo: null })}>Clear dates</SecondaryButton>
          )}
        </Popover>

        <SortField>
          <InlineLabel aria-hidden='true'>Sort</InlineLabel>
          <PillSelect value={sort} onChange={value => value && onSortChoose(value)} options={SORT_OPTIONS} searchable={false} ariaLabel='Sort remedials' />
        </SortField>

      </Fields>
      <ActiveChips>
        {filters.incident && (
          <IncidentChip>
            Only remedials from one incident
            <ChipClear type='button' onClick={() => onChange({ incident: null })} aria-label='Show remedials from every incident'>
              <CloseIcon style={{ fontSize: 18 }} aria-hidden='true' />
            </ChipClear>
          </IncidentChip>
        )}
        <InvolvesChip filters={filters} onChange={onChange} />
        <ResolvedChip filters={filters} onChange={onChange} />
        <RaisedChip filters={filters} onChange={onChange} />
        {filtered && (
          <ClearButton type='button' onClick={onClear}>
            <CloseIcon aria-hidden='true' style={{ fontSize: 16 }} />
            {filterCount > 0 ? `Clear filters (${filterCount})` : 'Clear filters'}
          </ClearButton>
        )}
      </ActiveChips>
    </Bar>
  )
}

export default TableFilters

const Bar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  background: #fff;
  border: 1px solid ${BORDER};
  border-radius: 12px;
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`

const TopRow = styled.div`
  display: contents;
  @media (max-width: 768px) {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    min-width: 0;
  }
`

const SearchWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  order: 2;
  flex: 1 1 200px;
  min-width: 160px;
  max-width: 360px;
  min-height: 40px;
  padding: 0 12px;
  border: 1px solid transparent;
  border-radius: 8px;
  background: #f4f4f5;
  transition: border-color 0.15s ease, background-color 0.15s ease;
  &:focus-within { border-color: ${ACCENT}; background: #fff; }
  @media (max-width: 768px) { order: 4; flex: 1 1 100%; min-width: 0; max-width: none; }
`

const SearchInput = styled.input`
  flex: 1;
  width: 100%;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  font: inherit;
  font-size: 15px;
  color: ${INK};
`

const FiltersToggle = styled(Pill)`
  display: none;
  @media (max-width: 768px) { display: inline-flex; }
`

const ExportButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  order: 3;
  flex-shrink: 0;
  min-height: 40px;
  margin-left: auto;
  padding: 0 14px;
  border: 1px solid ${BORDER};
  border-radius: 8px;
  background: #fff;
  color: ${INK};
  font: inherit;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  svg { font-size: 18px; }
  &:hover:not(:disabled) { background: #f4f4f5; }
  &:focus-visible { outline: 2px solid ${ACCENT}; outline-offset: 2px; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
  @media (max-width: 768px) {
    margin-left: 0;
    padding: 0 10px;
    span { display: none; }
  }
`

const ActiveChips = styled.div`
  display: contents;
  @media (max-width: 768px) {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    &:empty { display: none; }
  }
`

const Fields = styled.div`
  display: contents;
  @media (max-width: 768px) {
    display: ${({ $open }) => ($open ? 'flex' : 'none')};
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    padding-top: 4px;
    & > button { max-width: 100%; }
  }
`

const SeverityGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  @media (max-width: 768px) { width: 100%; }
`

const SeverityChip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 40px;
  padding: 0 12px;
  border: 1px solid ${BORDER};
  border-radius: 20px;
  background: #fff;
  color: ${INK};
  font: inherit;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  &:hover { border-color: ${INK}; }
  &:focus-visible { outline: 2px solid ${ACCENT}; outline-offset: 2px; }
`

const Dot = styled.span`
  width: 10px;
  height: 10px;
  border-radius: 50%;
`

const Divider = styled.span`
  width: 1px;
  height: 24px;
  margin: 0 4px;
  background: ${BORDER};
  @media (max-width: 768px) { display: none; }
`

const PillText = styled.span`
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
`

const SortField = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  @media (max-width: 768px) {
    width: 100%;
    & > button { flex: 1; max-width: none; }
  }
`

const PopTitle = styled.h4`
  margin: 0 0 4px;
  font-size: 16px;
  font-weight: 700;
  color: ${INK};
`

const PopHelp = styled.p`
  margin: 0 0 12px;
  font-size: 14px;
  line-height: 1.45;
  color: ${MUTED};
`

const DateField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 12px;
`

const DateLabel = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: ${INK};
`

const DateInput = styled.input`
  min-height: 44px;
  padding: 0 10px;
  border: 1px solid ${BORDER};
  border-radius: 8px;
  font: inherit;
  font-size: 15px;
`

const IncidentChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 40px;
  padding: 0 4px 0 12px;
  border-radius: 20px;
  background: #f4f4f5;
  font-size: 14px;
  color: ${INK};
`

const ChipClear = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: none;
  background: none;
  cursor: pointer;
`

const ClearButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  min-height: 40px;
  padding: 0 12px;
  border: none;
  border-radius: 20px;
  background: #f4f4f5;
  color: ${INK};
  font: inherit;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  &:hover { background: #e4e4e7; }
  &:focus-visible { outline: 2px solid ${INK}; outline-offset: 2px; }
`
